import React, { useContext, useEffect, useMemo, useState } from 'react';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { exportTablePdf, exportTableExcel, buildTablePdf } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { formatMoney, formatDate, primero, useLocalTable, today, daysAgo } from '../finanzas-cxp/shared.js';
import CapturaGastosCargosModal from './CapturaGastosCargosModal.jsx';

export const PUERTOS = ['Aeropuero CDMX', 'Laredo', 'Lazaro Cardenas', 'Manzanillo', 'Progreso', 'Tijuana', 'Veracruz'];
export const STATUS = [
  'Pendiente', 'BL', 'PL', 'Factura', 'Ficha Tecnica', 'Toque Piso', 'Telex', 'Naviera Freno Demoras', 'Revalidacion',
  'Pago Pedimento', 'Por Modular', 'Modulado', 'Por Documentar Tren', 'Por subir Tren', 'Maniobra Transportista',
  'Maniobra Racs', 'Maniobra Conia', 'Patio Racs', 'Patio Conia', 'Patio Transportista', 'Entregado', 'Cancelado',
];

/** Misma transformación de V1 (`ListadoContenedoresAdmin.jsx`) a fila de tabla. */
export function filaContenedor(a) {
  const naviera = primero(a.navieras); const cliente = primero(a.clientes); const agente = primero(a.agentes);
  const forwarder = primero(a.forwarders); const terminal = primero(a.terminales); const programa = primero(a.programas);
  return {
    _id: a._id,
    numero: a.idContenedor,
    fecha: a.fecha,
    observaciones: a.observaciones,
    totalGeneral: a.totalGeneral,
    navieras: naviera?.name || '', idNavieras: naviera?._id || '',
    clientes: cliente?.nombre_comercial || '', idClientes: cliente?._id || '',
    agentes: agente?.name || '', idAgentes: agente?._id || '',
    terminal: terminal?.name || '',
    programa: programa?.name || '',
    forwarders: forwarder?.name || '', idForwarders: forwarder?._id || '',
    totalCantidad: a.totalCantidad,
    numeroInterno: cliente?.nombre_comercial ? `${cliente.nombre_comercial}-${a.idContenedor}` : String(a.idContenedor),
    contenedor: a.contenedor || '',
    etaReal: a.etaReal,
    puerto: a.puerto || '',
    direccionEntrega: a.direccionEntrega,
    modalidad: a.modalidad || '',
    asegurado: a.asegurado || '',
    status: a.status || '',
    fechaSalidaPuerto: a.fechaSalidaPuerto,
    fechaSalidaRuta: a.fechaSalidaRuta,
    fechaEntregaCliente: a.fechaEntregaCliente,
  };
}

export default function ListadoContenedoresAdminPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('contenedores:ver');

  const [fechaInicio, setFechaInicio] = useState(daysAgo(91));
  const [fechaFin, setFechaFin] = useState(today());
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [conceptos, setConceptos] = useState([]);

  const [fNaviera, setFNaviera] = useState('');
  const [fCliente, setFCliente] = useState('');
  const [fAgente, setFAgente] = useState('');
  const [fForwarder, setFForwarder] = useState('');
  const [fPuerto, setFPuerto] = useState('');
  const [fAsegurado, setFAsegurado] = useState('');
  const [fStatus, setFStatus] = useState('');

  const [modal, setModal] = useState(null); // { tipo: 'gastos'|'cargos', fila }
  const [mailOpen, setMailOpen] = useState(false);

  useEffect(() => {
    if (!puedeVer) return;
    apiClient.get('/proveedores', { params: { limit: 500 } }).then((r) => setProveedores(r.data.data || [])).catch(() => {});
    apiClient.get('/clientes', { params: { limit: 500 } }).then((r) => setClientes(r.data.data || [])).catch(() => {});
    apiClient.get('/conceptos-gastos', { params: { limit: 500 } }).then((r) => setConceptos(r.data.data || [])).catch(() => {});
  }, [puedeVer]);

  async function cargar() {
    if (!puedeVer || !fechaInicio || !fechaFin) return;
    setCargando(true); setError(null);
    try {
      const { data } = await apiClient.get(`/contenedores/fechas/${fechaInicio}/${fechaFin}`);
      setFilas([...(data.data || [])].sort((a, b) => (a.idContenedor < b.idContenedor ? 1 : -1)).map(filaContenedor));
    } catch (e) {
      setError(e.message); setFilas([]);
    } finally {
      setCargando(false);
    }
  }
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [puedeVer, fechaInicio, fechaFin]);

  const filasFiltradas = useMemo(() => filas.filter((e) => (
    (!fNaviera || e.idNavieras === fNaviera)
    && (!fCliente || e.idClientes === fCliente)
    && (!fAgente || e.idAgentes === fAgente)
    && (!fForwarder || e.idForwarders === fForwarder)
    && (!fPuerto || e.puerto === fPuerto)
    && (!fAsegurado || e.asegurado === fAsegurado)
    && (!fStatus || e.status === fStatus)
  )), [filas, fNaviera, fCliente, fAgente, fForwarder, fPuerto, fAsegurado, fStatus]);

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(filasFiltradas, {
    searchFields: ['numeroInterno', 'contenedor', 'navieras', 'clientes', 'agentes', 'status', 'forwarders', 'totalCantidad', 'totalGeneral', 'numero'],
    perPage: 50,
  });

  const tipoProv = (t) => [...proveedores].filter((p) => p.tipo === t).sort((a, b) => (a.name > b.name ? 1 : -1));

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'etaReal', label: 'ETA', sortable: true },
    { key: 'numeroInterno', label: 'No. Interno', sortable: true },
    { key: 'contenedor', label: 'Contenedor', sortable: true },
    { key: 'navieras', label: 'Naviera', sortable: true },
    { key: 'clientes', label: 'Cliente', sortable: true },
    { key: 'agentes', label: 'Agente Aduanal', sortable: true },
    { key: 'forwarders', label: 'Forwarder', sortable: true },
    { key: 'puerto', label: 'Puerto', sortable: true },
    { key: 'modalidad', label: 'Modalidad', sortable: true },
    { key: 'asegurado', label: 'Asegurado', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    {
      key: '__acciones', label: 'Acciones', sortable: false,
      render: (r) => (
        <div className="flex gap-1">
          <button type="button" className="btn-primary text-xs px-2 py-1" onClick={() => setModal({ tipo: 'gastos', fila: r })}>Gastos</button>
          <button type="button" className="btn-outline text-xs px-2 py-1" onClick={() => setModal({ tipo: 'cargos', fila: r })}>Cargos</button>
        </div>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  // Columnas EXACTAS de V1 (`PDFTabla()` / `excel()`), distintas a las de la tabla en pantalla.
  const colsPdf = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'etaReal', label: 'ETA' },
    { key: 'numeroInterno', label: 'No. Interno' },
    { key: 'contenedor', label: 'Contenedor' },
    { key: 'navieras', label: 'Naviera' },
    { key: 'clientes', label: 'Cliente' },
    { key: 'agentes', label: 'Agente Aduanal' },
    { key: 'forwarders', label: 'Forwarder' },
    { key: 'puerto', label: 'Puerto' },
    { key: 'terminal', label: 'Terminal' },
    { key: 'modalidad', label: 'Modalidad' },
    { key: 'fechaSalidaPuerto', label: 'Salida Puerto' },
    { key: 'fechaSalidaRuta', label: 'Salida Ruta' },
    { key: 'status', label: 'Status' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'programa', label: 'Programa' },
    { key: 'totalCantidad', label: 'Cantidad' },
    { key: 'totalGeneral', label: 'Total', render: (r) => formatMoney(r.totalGeneral) },
    { key: 'direccionEntrega', label: 'Direccion Entrega' },
  ];
  const colsExcel = [
    ['numero', 'Numero'], ['fecha', 'Fecha'], ['etaReal', 'ETA'], ['numeroInterno', 'NumeroInterno'], ['contenedor', 'Contenedor'],
    ['navieras', 'Naviera'], ['clientes', 'Cliente'], ['agentes', 'AgenteAduanal'], ['forwarders', 'Forwarder'], ['puerto', 'Puerto'],
    ['terminal', 'Terminal'], ['modalidad', 'Modalidad'], ['fechaSalidaPuerto', 'FechaSalidaPuerto'], ['fechaSalidaRuta', 'FechaSalidaRuta'],
    ['fechaEntregaCliente', 'FechaEntregaCliente'], ['asegurado', 'Asegurado'], ['status', 'Status'], ['observaciones', 'Observaciones'],
    ['programa', 'Programa'], ['direccionEntrega', 'DireccionEntrega'],
  ].map(([key, label]) => ({ key, label }));

  const opcionesPdf = { subtitulo: `${fechaInicio} - ${fechaFin}`, fileName: 'Contenedores' };
  const hacerPdf = () => exportTablePdf('Listado Contenedores', colsPdf, filtradas, opcionesPdf);
  // eMail (V1 `enviaMail`): mismo PDF; destinatario inicial vacío como V1 (`mailTo`).
  const enviarMail = async (email) => {
    const doc = await buildTablePdf('Listado Contenedores', colsPdf, filtradas, opcionesPdf);
    await enviarCorreoAdjunto({
      subject: 'Listado Contenedores', email, fileName: 'ListadoContenedores.pdf', docPdf: doc,
      textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Contenedores.'),
    });
  };
  const hacerExcel = () => exportTableExcel('ListadoContenedores', colsExcel, filtradas, { fileName: 'ListadoContenedores', sheetName: 'ListadoContenedores' });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Contenedores</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={hacerPdf} className="btn-outline text-xs px-3 py-1.5">PDF</button>
          <button type="button" onClick={() => setMailOpen(true)} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={hacerExcel} className="btn-outline text-xs px-3 py-1.5">Excel</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="input-base" />
        </div>
        <div className="ml-auto">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="input-base w-60" />
        </div>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div>
          <label className="label">Naviera</label>
          <select value={fNaviera} onChange={(e) => { setFNaviera(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {tipoProv('Naviera').map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={fCliente} onChange={(e) => { setFCliente(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {[...clientes].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Agente Aduanal</label>
          <select value={fAgente} onChange={(e) => { setFAgente(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {tipoProv('Agente').map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Forwarder</label>
          <select value={fForwarder} onChange={(e) => { setFForwarder(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {tipoProv('Forwarder').map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Puerto</label>
          <select value={fPuerto} onChange={(e) => { setFPuerto(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {PUERTOS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Asegurado</label>
          <select value={fAsegurado} onChange={(e) => { setFAsegurado(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            <option value="No">No</option>
            <option value="Si">Si</option>
            <option value="No Aplica">No Aplica</option>
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Contenedores" onEnviar={enviarMail} />

      <CapturaGastosCargosModal
        open={!!modal} onClose={() => setModal(null)} tipo={modal?.tipo} contenedor={modal?.fila}
        proveedores={proveedores} conceptos={conceptos} clientes={clientes} onGuardado={cargar}
      />
    </div>
  );
}
